import {DataFrame} from 'data-frames/DataFrameTypes';
import {getNumberColumnValues} from 'data-frames/util/ColumnSummaries';
import findAndAssertColumn from 'data-frames/util/findAndAssertColumn';
import {AnalyticsVisualizationBarChartType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {
  BarChartConfig,
  BAR_CHART_TYPE_VALIDATION_MAP,
  BarChartProps,
  BarChartType,
} from 'ui-library/charting/BarChart';
import {ERROR_BAR_TYPE_TO_LABEL} from 'ui-library/charting/statistics/aggregation/Constants';
import getAggregatedAxisName from 'ui-library/charting/statistics/aggregation/getAggregatedAxisName';
import {AxisScale as ChartingAxisScale} from 'ui-library/charting/Types';

import cleanAndValidateDataFrame from './cleanAndValidateDataFrame';
import ConvertToChartPropsException from './ConvertToChartPropsException';
import maybeConvertProps from './maybeConvertProps';
import {ColumnInfo, ConversionOutput, SuccessfulConversionOutput} from './PropConverterTypes';
import findAxisColumn from './utils/findAxisColumn';
import {getDataFrameColumnsMapping} from './utils/getDataFrameColumnsMapping';

type BarChartParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationBarChartParametersNode'
>;

export const INSIGHTS_BAR_CHART_TYPE_TO_CHARTING_ENUM: {
  [key in AnalyticsVisualizationBarChartType]: BarChartType;
} = {
  [AnalyticsVisualizationBarChartType.STACKED]: BarChartType.STACKED,
  [AnalyticsVisualizationBarChartType.STANDARD]: BarChartType.GROUPED,
};

export const NEGATIVE_STACKED_BAR_CHART_VALUES_ERROR = 'All y-values in a stacked bar chart must be positive';

export function getBarChartColumnInfos(parameters: BarChartParameters): Array<ColumnInfo> {
  return [
    {
      chartDataColumnName: 'x',
      inputDataColumnSystemName: parameters.xAxisColumnName,
      typeValidationOptions: BAR_CHART_TYPE_VALIDATION_MAP.x,
      cleanupNulls: true,
    },
    {
      chartDataColumnName: 'y',
      inputDataColumnSystemName: parameters.yAxisColumnName,
      typeValidationOptions: BAR_CHART_TYPE_VALIDATION_MAP.y,
      cleanupNulls: true,
    },
    ...(parameters.seriesColumnName == null
      ? []
      : [
          {
            chartDataColumnName: 'series',
            inputDataColumnSystemName: parameters.seriesColumnName,
            typeValidationOptions: BAR_CHART_TYPE_VALIDATION_MAP.series,
            cleanupNulls: true,
          },
        ]),
  ];
}

/** @package */
export function validateSeriesValues(
  dataFrame: DataFrame,
  {xAxisColumnName, seriesColumnName}: BarChartParameters
): void {
  if (seriesColumnName == null) {
    return;
  }

  const uniqueSeriesNames = new Set(dataFrame.rows.map((row) => row[seriesColumnName]));

  // There's a condition in BarChart where if the series column and x-axis column have the same system names
  // as one of the values in that column, we get a failed render that crashes the Insights UI because of a
  // namespace collision in `convertDataFrameToBarGroupDicts`.
  //
  // This was a regression in CUST-7758.
  if (uniqueSeriesNames.has(xAxisColumnName)) {
    const xAxisColumn = findAndAssertColumn(dataFrame, xAxisColumnName);
    throw new ConvertToChartPropsException(
      `Series column cannot contain values that are to similar the x-axis column's name.
      The value "${xAxisColumnName}" is too similar to the x-axis column name "${xAxisColumn.displayName}".`
    );
  }
}

/** @package */
export function validateStackPositiveValues(
  dataFrame: DataFrame,
  {barChartType, yAxisColumnName}: Pick<BarChartParameters, 'barChartType' | 'yAxisColumnName'>
): void {
  if (barChartType !== AnalyticsVisualizationBarChartType.STACKED) {
    return;
  }

  const systemName = yAxisColumnName;

  if (getNumberColumnValues(dataFrame, systemName).some((value) => value < 0)) {
    throw new ConvertToChartPropsException(NEGATIVE_STACKED_BAR_CHART_VALUES_ERROR);
  }
}

function cleanAndValidateBarChartData(
  dataFrame: DataFrame,
  columnInfos: Array<ColumnInfo>,
  parameters: BarChartParameters
): DataFrame {
  const cleanDataFrame = cleanAndValidateDataFrame(dataFrame, columnInfos, VisualizationType.BAR_CHART);
  validateSeriesValues(dataFrame, parameters);
  validateStackPositiveValues(dataFrame, parameters);

  return cleanDataFrame;
}

function shouldBarChartIncludeZero({yAxisMin, yAxisMax}: BarChartParameters): boolean {
  return (
    (yAxisMin == null && yAxisMax == null) ||
    (yAxisMin != null && yAxisMax == null && yAxisMin <= 0) ||
    (yAxisMax != null && yAxisMin == null && yAxisMax >= 0)
  );
}

export function convertBarChartConfig(dataFrame: DataFrame, parameters: BarChartParameters): BarChartConfig {
  const xAxisLabel = findAxisColumn(dataFrame, parameters.xAxisColumnName).displayName;
  const yAxisLabel = findAxisColumn(dataFrame, parameters.yAxisColumnName).displayName;
  const seriesLabel =
    parameters.seriesColumnName != null
      ? findAxisColumn(dataFrame, parameters.seriesColumnName).displayName
      : '';

  return {
    ...(parameters.seriesColumnName ? {legend: {label: seriesLabel}} : {}),
    ...(parameters.errorBarType && {
      errorBarConfig: {errorValueLabel: ERROR_BAR_TYPE_TO_LABEL[parameters.errorBarType]},
    }),
    type: INSIGHTS_BAR_CHART_TYPE_TO_CHARTING_ENUM[parameters.barChartType],
    xAxis: {
      label: xAxisLabel,
    },
    yAxis: {
      includeZero: shouldBarChartIncludeZero(parameters),
      label: getAggregatedAxisName(parameters.aggregationType, yAxisLabel),
      // Although bar chart is modeled to have yAxisScale config, we don't support it in the ui, so it's unused.
      // There are cases where yAxisScale is set to non-LINEAR, which is when a user changes from a logs scatter
      // plot to a bar chart since we copy over configs from chart-to-chart when changing chart types.
      //
      // Since we always want LINEAR scale, we just hardcode it here in stead of using yAxisScale.
      scale: ChartingAxisScale.LINEAR,
      ...(parameters.yAxisMin != null && {min: parameters.yAxisMin}),
      ...(parameters.yAxisMax != null && {max: parameters.yAxisMax}),
    },
  };
}

export function convertBarChartProps(
  dataFrame: DataFrame,
  parameters: BarChartParameters
): SuccessfulConversionOutput<BarChartProps> {
  const columnInfos = getBarChartColumnInfos(parameters);

  return {
    valid: true,
    props: {
      dataFrame: cleanAndValidateBarChartData(dataFrame, columnInfos, parameters),
      columnsMapping: getDataFrameColumnsMapping(dataFrame, columnInfos),
      config: convertBarChartConfig(dataFrame, parameters),
    },
  };
}

export default function maybeConvertBarChartProps(
  dataFrame: DataFrame,
  parameters: BarChartParameters
): ConversionOutput<BarChartProps> {
  return maybeConvertProps(() => convertBarChartProps(dataFrame, parameters));
}
